import axios, { type AxiosProgressEvent } from 'axios'
import { api } from '@/lib/api'
import type { OrderItem, OrderItemArtwork } from '@/types'

/** What POST /orders/artwork returns and an order item stores. */
export interface Artwork {
  artwork_name: string
  artwork_path: string
  artwork_key: string
  artwork_size: number
  artwork_mime: string
}

// No SVG: it is scriptable XML, and uploads are served from this origin, so
// the API refuses it too. Vector work goes in as PDF, AI or EPS.
export const ARTWORK_ACCEPT = '.pdf,.ai,.eps,.png,.jpg,.jpeg'

/**
 * What the upload takes, in words, shown under every artwork picker. Keep it
 * matched to ArtworkController on the API (ArtworkController::TYPES and
 * MAX_KB, 100 MB), which is the real limit.
 */
export const ARTWORK_HINT = 'PDF, AI, EPS, PNG or JPG, up to 100 MB'

/** ArtworkController::MAX_KB, in bytes. Checked here too so a 300 MB file is refused before it uploads. */
export const MAX_ARTWORK_BYTES = 100 * 1024 * 1024

/** Called with 0-100 while a file uploads. */
export type UploadProgress = (percent: number) => void

/**
 * A refusal worded for the person uploading (too big, or the bucket could not
 * be reached), told apart from an API reply, which errorMessage() reads.
 */
export class ArtworkUploadError extends Error {}

type DirectUpload = { direct: false } | { direct: true; key: string; upload_url: string; headers: Record<string, string> }

/**
 * Upload a print file.
 *
 * The same endpoints are registered under three prefixes, so basePath says
 * which console is asking: '' is the customer checkout (the product page only
 * calls it for a signed-in customer; guests attach at checkout instead), and
 * '/admin' or '/sales' are the manual order form.
 *
 * On object storage the file goes straight into the bucket (Duncan
 * 2026-11-03: 100 MB print files), never through Cloudflare's 100 MB request
 * cap or our server: the API names the key and gives a presigned URL, the
 * browser PUTs, and the API checks what arrived. On a local disk the API says
 * direct: false and the file is posted to it as before.
 */
export async function uploadArtwork(file: File, basePath = '', onProgress?: UploadProgress): Promise<Artwork> {
  if (file.size > MAX_ARTWORK_BYTES) {
    throw new ArtworkUploadError(`${file.name} is ${fileSize(file.size)}. Artwork can be up to 100 MB.`)
  }
  const progress = (event: AxiosProgressEvent) => {
    if (onProgress && event.total) onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
  }

  const plan = (await api.post<DirectUpload>(`${basePath}/orders/artwork/direct`, { name: file.name, size: file.size })).data

  if (!plan.direct) {
    const form = new FormData()
    form.append('artwork', file)
    return (await api.post<Artwork>(`${basePath}/orders/artwork`, form, { onUploadProgress: progress })).data
  }

  // A bare request, not the api instance: the bucket must never see our
  // bearer token, and its errors are not our API's.
  try {
    await axios.put(plan.upload_url, file, { headers: plan.headers, onUploadProgress: progress })
  } catch {
    throw new ArtworkUploadError(`${file.name} did not upload to the file storage. Check the connection and try again.`)
  }

  return (await api.post<Artwork>(`${basePath}/orders/artwork/direct/confirm`, { key: plan.key, name: file.name })).data
}

/** Files per order item; matches StoreOrderRequest::MAX_ARTWORKS on the API. */
export const MAX_ARTWORKS = 6

/**
 * The font behind an item's names and numbers (client request 2026-10-14:
 * the floor's size layout needs the actual font). Required at checkout on a
 * line with names; optional on the team's manual order. Matches
 * FontController on the API (extensions ttf, otf, zip; max 20480 KB).
 */
export const FONT_ACCEPT = '.ttf,.otf,.zip'
export const FONT_HINT = 'TTF, OTF or a ZIP of several, up to 20 MB'
/** Matches StoreOrderRequest::MAX_FONTS on the API. */
export const MAX_FONTS = 3

/**
 * Upload a font file. Same three prefixes as uploadArtwork. The reply is
 * read into the Artwork shape so pickers, cart lines and thumbnails handle
 * fonts without a second set of types; fontPayload() turns it back into the
 * API's font_* names.
 */
export async function uploadFont(file: File, basePath = ''): Promise<Artwork> {
  const form = new FormData()
  form.append('font', file)
  const { data } = await api.post<{ font_name: string; font_path: string; font_key: string; font_size: number; font_mime: string }>(
    `${basePath}/orders/font`,
    form,
  )
  return { artwork_name: data.font_name, artwork_path: data.font_path, artwork_key: data.font_key, artwork_size: data.font_size, artwork_mime: data.font_mime }
}

/** An order line's font files for the API: keys only, like artworkPayload. */
export function fontPayload(files: Artwork[]) {
  return { fonts: files.map((file) => ({ font_name: file.artwork_name, font_key: file.artwork_key })) }
}

/** An order item's font files as uploads, for a picker editing them (Edit font, Buy again). */
export function itemFontUploads(item: OrderItem): Artwork[] {
  return (item.fonts ?? [])
    .filter((file) => file.key && file.path)
    .map((file) => ({ artwork_name: file.name ?? 'font', artwork_path: file.path ?? '', artwork_key: file.key ?? '', artwork_size: file.size ?? 0, artwork_mime: file.mime ?? '' }))
}

/**
 * An order line's artwork for the API: every file in order (front, back,
 * ...), or, with none uploaded, the bare filename the product page recorded,
 * which the server keeps so the job sheet can say a file is still owed.
 * Only keys go up; the server derives path, size and mime from them.
 */
export function artworkPayload(files: Artwork[], fallbackName?: string | null) {
  return files.length > 0
    ? { artworks: files.map((file) => ({ artwork_name: file.artwork_name, artwork_key: file.artwork_key })) }
    : { artwork_name: fallbackName ?? null }
}

/**
 * An order item's files in order. A payload from before several files
 * existed has no `artworks`, so its one artwork_* file stands in.
 */
export function itemArtworks(item: OrderItem): OrderItemArtwork[] {
  if (item.artworks) return item.artworks
  if (!item.artwork_path && !item.artwork_name) return []

  return [{ name: item.artwork_name ?? null, path: item.artwork_path ?? null, size: item.artwork_size ?? null, mime: item.artwork_mime ?? null, key: item.artwork_key ?? null }]
}

/** An upload (picker, cart line) in the shape the order page's thumbnails read. */
export function asItemArtwork(file: Artwork): OrderItemArtwork {
  return { name: file.artwork_name, path: file.artwork_path, size: file.artwork_size, mime: file.artwork_mime }
}

/** "2.4 MB" / "380 KB", or null when the size is unknown. */
export function fileSize(bytes?: number | null): string | null {
  if (!bytes) return null

  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * What a browser can draw of an artwork file.
 *
 * - png / jpg render in an <img> (svg too, on orders old enough to carry one;
 *   the upload no longer accepts it).
 * - pdf is rasterized with pdf.js. Modern .ai files are PDF-compatible and
 *   the server stores them with the pdf mime, so those preview for free.
 * - ai / eps without that mime cannot be drawn at all.
 */
export function artworkKind(mime?: string | null, name?: string | null, url?: string | null): 'image' | 'pdf' | 'other' {
  if (mime === 'application/pdf') return 'pdf'
  // Postscript (.ai/.eps) reports image/* in some browsers' mime tables but
  // never renders in an <img>, so the extension check below has the final say.
  const probe = `${name ?? ''} ${url ?? ''}`.toLowerCase()
  if (/\.(ai|eps)(\s|\?|$)/.test(probe)) return 'other'
  if (/\.pdf(\s|\?|$)/.test(probe)) return 'pdf'
  if (mime?.startsWith('image/') || /\.(png|jpe?g|svg)(\s|\?|$)/.test(probe)) return 'image'
  return 'other'
}

/** The type label a person knows a file by: PDF, AI, EPS, PNG, JPG. */
export function artworkExt(file: { name?: string | null; path?: string | null; mime?: string | null }): string {
  const match = /\.([a-z0-9]{2,4})$/i.exec(file.name ?? '') ?? /\.([a-z0-9]{2,4})(\?|$)/i.exec(file.path ?? '')
  if (match) {
    const ext = match[1].toUpperCase()
    return ext === 'JPEG' ? 'JPG' : ext
  }
  return file.mime === 'application/pdf' ? 'PDF' : 'FILE'
}

export interface PdfPreview {
  dataUrl: string
  pages: number
}

// One promise per file and width, so a re-render, a second tile or reopening
// the preview never downloads and decodes the same PDF twice.
const pdfPreviews = new Map<string, Promise<PdfPreview>>()

/**
 * Page 1 of a PDF as a PNG data URL, about `width` pixels wide: ~1400 for
 * print and the preview dialog, a few hundred for a thumbnail.
 */
export function renderPdfFirstPage(url: string, width = 1400): Promise<PdfPreview> {
  const cacheKey = `${url}@${width}`
  let preview = pdfPreviews.get(cacheKey)
  if (!preview) {
    preview = rasterizePdf(url, width)
    // A failure is not cached: the next attempt (a reopened dialog) retries.
    preview.catch(() => pdfPreviews.delete(cacheKey))
    pdfPreviews.set(cacheKey, preview)
  }
  return preview
}

async function rasterizePdf(url: string, width: number): Promise<PdfPreview> {
  // Dynamic imports keep pdf.js (~400 KB) out of the main bundle; only a page
  // that actually shows a PDF pays for it.
  const pdfjs = await import('pdfjs-dist')
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default

  const task = pdfjs.getDocument({ url })
  try {
    const doc = await task.promise
    const page = await doc.getPage(1)
    const base = page.getViewport({ scale: 1 })
    const scale = Math.min(4, Math.max(0.25, width / base.width))
    const viewport = page.getViewport({ scale })

    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(viewport.width)
    canvas.height = Math.ceil(viewport.height)
    const canvasContext = canvas.getContext('2d')
    if (!canvasContext) throw new Error('canvas 2d context unavailable')

    await page.render({ canvas, canvasContext, viewport }).promise
    return { dataUrl: canvas.toDataURL('image/png'), pages: doc.numPages }
  } finally {
    void task.destroy()
  }
}

/**
 * Save the file under the customer's own filename. Stored keys are random
 * (artwork/aB3x...jpeg), and a cross-origin <a download> is ignored by
 * browsers, so the bytes are fetched and saved from a blob. Where the fetch is
 * refused (a bucket without a CORS rule) the file opens in a tab instead.
 */
export async function downloadArtwork(file: { name?: string | null; path: string }): Promise<void> {
  try {
    const response = await fetch(file.path)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const blobUrl = URL.createObjectURL(await response.blob())
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = file.name ?? file.path.split('/').pop() ?? 'artwork'
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
  } catch (error) {
    console.error('artwork download failed, opening instead', error)
    window.open(file.path, '_blank', 'noopener')
  }
}
